
<script setup lang="ts">
import {
  IonContent,
  IonFab,
  IonFabButton,
  IonLoading,
  IonSpinner,
  IonPage,
  IonIcon,
  useIonRouter,
} from '@ionic/vue';
import BSlideHeader from '@/components/BSlideHeader.vue';
import Globe from '@/components/illustrations/Globe.vue';
import BGoalPlanItem from '@/components/BGoalPlanItem.vue';
import BButton from '@/components/BButton.vue';
import { computed, provide, ref, onMounted, type Ref } from 'vue';
import { homeSharp, arrowForward } from 'ionicons/icons';
import { useStore } from '@/stores/main';
import type { GoalPlan } from '@/types';
import { useI18n } from 'vue-i18n';

interface Factor {
  category_id: number;
  label: string;
  value: number;
  vision: number;
  affected_by_factors: Factor[];
  factors: Factor[];
}

const router = useIonRouter();
const store = useStore();
const loading = ref(false);
const saving = ref(false);
const goals = ref<Factor[]>([]);
const savedGoals = ref<Record<number, GoalPlan>>({});
const contentRef: Ref<HTMLIonContentElement | null> = ref(null);

const { t } = useI18n();

provide('pageData', {
  number: 4,
  title: 'Min livsstilsplan',
  icon: Globe,
});

provide('colorScheme', 'yellow');

onMounted(async () => {
  loading.value = true;
  try {
    const fetchedGoals = await store.fetchPlannedGoals();
    goals.value = fetchedGoals as Factor[] || [];
    if (!goals.value || goals.value.length === 0) {
      let firstGoalId: number | null = null;
      for (let attempt = 0; attempt < 2 && !firstGoalId; attempt++) {
        const planned = await store.fetchPlannedGoals();
        if (planned && planned.length > 0) {
          firstGoalId = planned[0].category_id;
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 250));
      }

      if (!firstGoalId) {
        const allGoals = await store.fetchGoals();
        const improving = (allGoals || []).filter((g: any) => Number(g.selected) === 1 && Number(g.vision) > Number(g.value));
        if (improving.length > 0) {
          firstGoalId = improving[0].category_id;
        }
      }

      if (firstGoalId) {
        window.location.href = `/plan/${firstGoalId}/actions`;
      }
    }
  } catch (error) {
    console.error('Failed to fetch goals:', error);
  } finally {
    loading.value = false;
  }
});

function goHome() {
  router.replace('/evaluate');
}

function planSaved(plan: GoalPlan) {
  savedGoals.value[plan.categoryId] = plan;
}

const canContinue = computed(
  () => goals.value.length > 0
);

const progressPercentage = computed(() => {
  if (!goals.value.length) return 0;
  return 100; // Always show 100% since we're just navigating to actions
});

async function save() {
  saving.value = true;
  try {
    let firstGoalId: number | null = null;

    if (goals.value.length > 0) {
      firstGoalId = goals.value[0].category_id;
    } else {
      for (let attempt = 0; attempt < 3 && !firstGoalId; attempt++) {
        const planned = await store.fetchPlannedGoals();
        if (planned && planned.length > 0) {
          firstGoalId = planned[0].category_id;
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      if (!firstGoalId) {
        const allGoals = await store.fetchGoals();
        const improving = (allGoals || []).filter((g: any) => Number(g.selected) === 1 && Number(g.vision) > Number(g.value));
        if (improving.length > 0) {
          firstGoalId = improving[0].category_id;
        }
      }
    }

    if (firstGoalId) {
      window.location.href = `/plan/${firstGoalId}/actions`;
    } else {
      window.location.href = '/goals/improve';
    }
  } catch (error) {
    console.error('Failed to navigate to actions page:', error);
    window.location.href = '/goals/improve';
  } finally {
    saving.value = false;
  }
}
</script>


<template>
  <ion-page>
    <!-- Fixed header -->
    <div class="fixed-header">
      <b-slide-header />
    </div>

    <ion-content 
      ref="contentRef"
      :fullscreen="false"
      :scroll-y="true"
      class="plans-page-content"
    >
      <!-- Home button -->
      <ion-fab slot="fixed" class="home-button">
        <ion-fab-button @click="goHome" color="light" size="small">
          <ion-icon :icon="homeSharp" />
        </ion-fab-button>
      </ion-fab>
      
      <!-- Loading indicators -->
      <ion-loading :is-open="loading" :message="$t('goals.loadingGoals')" />
      <ion-loading :is-open="saving" :message="$t('goals.savingGoals')" />
      
      <!-- Main content -->
      <div class="content-wrapper">
        <h1 class="page-title">Äntligen sista steget</h1>
        <p class="main-description">
          Nu ska du bara bestämma dig för HUR du ska nå dina MÅL. Lugn, jag vägleder dig hela vägen. Vi tar ett MÅL i taget!
        </p>

        <!-- Loading state -->
        <div v-if="loading" class="loading-container">
          <ion-spinner name="dots" />
          <span class="loading-text">{{ t('goals.loadingGoals') }}</span>
        </div>
        
        <!-- Empty state -->
        <div v-else-if="goals.length === 0" class="empty-state">
          <div class="empty-state-content">
            <p>{{ t('goals.noGoalsSelected') }}</p>
            <BButton class="mt-4" @click="goHome">{{ t('goals.goToEvaluation') }}</BButton>
          </div>
        </div>
        
        <!-- Goals display -->
        <template v-else>
          <!-- Remove the intro-section div with the text -->
          
          <!-- Goals list -->
          <ul class="goals-list">
            <b-goal-plan-item
              v-for="factor in goals"
              :key="factor.category_id"
              :category-id="factor.category_id"
              :data="factor"
              :vision="factor.vision"
              :original-value="factor.value"
            >
              {{ factor.label }}
            </b-goal-plan-item>
          </ul>

          <!-- Action buttons -->
          <div class="action-buttons">
            <BButton 
              v-if="canContinue" 
              @click="save" 
              class="continue-button"
            >
              {{ t('navigation.next') }}
              <ion-icon :icon="arrowForward" class="ml-2" />
            </BButton>
            
            <p v-if="goals.length > 0 && !canContinue" class="help-text">
              {{ t('goals.fillAllGoals') }}
            </p>
          </div>
        </template>
      </div>
    </ion-content>
  </ion-page>
</template>

<style scoped>
/* Fixed header styles */
.fixed-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  background-color: #FFFBEE;
  height: 80px;
}

/* Ionic content styles */
.plans-page-content {
  --background: #FFFBEE;
  --padding-top: 80px;
  --padding-bottom: 0;
  position: relative;
}

/* Content wrapper */
.content-wrapper {
  padding: 20px 1.5rem;
  min-height: 100%;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.home-button {
  position: fixed;
  right: 1rem;
  top: 90px;
  z-index: 100;
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #333;
  text-align: center;
}

.main-description {
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  color: #4a6572;
  text-align: center;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.loading-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
}

.loading-text {
  color: #b08d00;
  font-weight: 500;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  text-align: center;
}

.empty-state-content {
  font-size: 1.1rem;
}

.intro-section {
  margin-bottom: 2rem;
}

.intro-section p {
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  color: #4a6572;
}

.goals-list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  gap: 1rem;
  list-style: none;
}

.action-buttons {
  margin: 2rem 0;
  padding-bottom: 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.continue-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  font-size: 1.1rem;
  background-color: #f1c40f;
  color: #333;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.continue-button:hover {
  background-color: #d4aa00;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.help-text {
  margin-top: 1rem;
  color: #666;
  font-style: italic;
}

/* iOS scrolling improvements */
ion-content {
  --overflow: auto;
  --keyboard-offset: 0 !important;
}

ion-content::part(scroll) {
  overscroll-behavior: auto;
  -webkit-overflow-scrolling: touch;
  overflow-y: auto;
}

/* Prevent accidental highlighting */
* {
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
}

button {
  -webkit-user-select: none;
  user-select: none;
}

/* Responsive adjustments */
@media (min-width: 768px) {
  .content-wrapper {
    padding: 20px 2rem;
  }
  
  .home-button {
    right: 5%;
    top: 6%;
  }
}

@media (min-width: 1024px) {
  .home-button {
    top: 4%;
  }
  
  .action-buttons {
    align-items: flex-start;
  }
}

/* Ensure proper height calculation */
.ion-page {
  height: 100%;
}

/* Fix for touch scrolling */
.goals-list,
.intro-section,
.action-buttons {
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
}
</style>